import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { AUTH_PREFIX_PATH, APP_PREFIX_PATH } from "configs/AppConfig";
import {
  needsOnboardingAgreement,
  ONBOARDING_AGREEMENT_PATH,
} from "utils/roleHelpers";
import { needsSubscription } from "utils/subscriptionHelpers";

const RoleBasedRoute = ({
  allowedRoles = [],
  requiresVerification = false,
}) => {
  const { token, userType, userData, isProfileCompleted } = useSelector(
    (state) => state.auth
  );
  const location = useLocation();

  // Not authenticated - redirect to login
  if (!token) {
    return (
      <Navigate
        to={`${AUTH_PREFIX_PATH}/login`}
        replace
        state={{ from: location }}
      />
    );
  }

  if (!userData || !userType) {
    return <Outlet />; // Temporary - will be redirected by next check
  }

  if (!userData.verified) {
    return <Navigate to={`${AUTH_PREFIX_PATH}/otp-input`} replace />;
  }

  // Step 2: Profile Completion
  if (!isProfileCompleted) {
    return <Navigate to={`${AUTH_PREFIX_PATH}/profile-setup`} replace />;
  }

  // Step 3: DocuSign carrier onboarding agreement (feature-flagged, role-gated).
  // Mirrors the app's AppNavigator gate — carrier roles must e-sign before the
  // dashboard. DRIVER is excluded by role inside needsOnboardingAgreement.
  if (needsOnboardingAgreement(userData)) {
    return <Navigate to={ONBOARDING_AGREEMENT_PATH} replace />;
  }

  // Step 4: Subscription (paid roles only — DRIVER is exempt). Mirrors the
  // app's AppNavigator `needsSubscription` gate. Checks BOTH the mobile
  // (RevenueCat) and web (Stripe) subscription — either one unlocks this.
  // Already on the subscription-plan page itself — don't redirect-loop.
  if (
    needsSubscription(userData) &&
    location.pathname !== `${AUTH_PREFIX_PATH}/subscription-plan`
  ) {
    return <Navigate to={`${AUTH_PREFIX_PATH}/subscription-plan`} replace />;
  }

  // Check role authorization
  if (allowedRoles.length > 0 && !allowedRoles.includes(userType)) {
    // Redirect to role-specific dashboard
    const dashboardMap = {
      truck_owner: `${APP_PREFIX_PATH}/truck_owner/home`,
      mechanic: `${APP_PREFIX_PATH}/mechanic/dashboard`,
      fleet_manager: `${APP_PREFIX_PATH}/fleet-dashboard/home`,
      driver: `${APP_PREFIX_PATH}/driver/home`,
    };

    const redirectPath = dashboardMap[userType] || `${AUTH_PREFIX_PATH}/login`;

    return <Navigate to={redirectPath} replace />;
  }

  // All checks passed - allow access
  return <Outlet />;
};

export default RoleBasedRoute;
